import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, of } from 'rxjs';
import { map, catchError, finalize, tap } from 'rxjs/operators';
import { LoaderService } from './loader.service';
import { ToastService } from './toast.service';

/**
 * Filtre via relation Eloquent (whereHas), aligné sur filterWherePivot côté API :
 * si la valeur est une date valide → whereDate(key, operator, date), sinon → where LIKE %value%.
 */
export interface BreadPivotFilter {
  relation_name: string;
  key: string;
  value: string | number;
  /** Pour whereDate uniquement (ex. '=', '>=', '<='). */
  operator?: string;
}

export interface BreadFilter {
  relations?: string;
  simple?: { key: string; value: any; logic?: 'AND' | 'OR' }[];
  simplelike?: { key: string; value: string; logic?: 'AND' | 'OR' }[];
  range?: { key: string; min: any; max: any }[];
  where?: any;
  pivot?: BreadPivotFilter[];

  [filterType: string]: any;
}

export interface BreadOptions {
  page?: number;
  perPage?: number;
  paginate?: boolean;
  filters?: BreadFilter;
  prefix?: string;
  skipLoader?: boolean;
  endpoint?: string;
  beforeHook?: () => void;
  afterHook?: () => void;
  mapResponse?: (res: any) => any;
  onError?: (error: any) => void;
}

export interface Paginated<T> {
  items: T[];
  data?: T[];
  total: number;
  perPage: number;
  currentPage: number;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class BreadService {
  constructor(
    private api: ApiService,
    private loader: LoaderService,
    private toast: ToastService,
  ) {}

  // ------------------------------
  // BROWSE (overloads + impl)
  // ------------------------------
  browse<T>(model: string, opts: BreadOptions & { paginate: false }): Observable<T[]>;
  browse<T>(model: string, opts?: BreadOptions & { paginate?: true }): Observable<Paginated<T>>;
  browse<T>(model: string, opts: BreadOptions = {}): Observable<T[] | Paginated<T>> {
    
    const {
      page = 1,
      perPage = 10,
      paginate = true,
      filters = {},
      prefix = 'monicase',
      endpoint = '/browse/',
      skipLoader = false,
      beforeHook, afterHook, mapResponse, onError
    } = opts;

    const endpointPrefix = `${prefix}${endpoint}`;
    const useLoader = !skipLoader;

    const apiFilters: any = { ...filters };
    if (paginate) {
      apiFilters.paginate = 1;
      apiFilters.perpage = perPage;
    }
    const url = `${endpointPrefix}${model}${paginate ? `?page=${page}` : ''}`;

    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();
    return this.api.fetch<T>(url, { filters:  apiFilters, paginate, perPage: String(perPage) }).pipe(
      map(res => {
        if (mapResponse) return mapResponse(res);

        if (paginate && (res as any)?.data?.data) {
          const d = (res as any).data;
          return {
            items: d.data as T[],
            total: d.total,
            perPage: d.per_page,
            currentPage: d.current_page,
            ...d,
          } as Paginated<T>;
        }

        return (((res as any)?.data) as T[]) ?? [];
      }),
      tap({
        error: err => {
          this.toast.show('Erreur lors de la requête', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        // Valeur de repli cohérente selon paginate
        return paginate
          ? of({ items: [], total: 0, perPage, currentPage: page } as Paginated<T>)
          : of([] as T[]);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }

  // ------------------------------
  // READ (unitaire)
  // ------------------------------
  read<T>(model: string, id: number | string, opts: Partial<BreadOptions> = {}): Observable<T> {
    const { prefix = 'monicase', endpoint = '/read/', filters = {}, skipLoader = false, beforeHook, afterHook, mapResponse, onError } = opts;

    const endpointPrefix = `${prefix}${endpoint}`;
    const useLoader = !skipLoader;

    const apiFilters: any = { ...filters };

    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();

    const url = `${endpointPrefix}${model}/${id}`;

    return this.api.fetch<T>(url, { filters: apiFilters }).pipe(
      map(res => (mapResponse ? mapResponse(res) : ((res as any).data as T))),
      tap({
        error: err => {
          this.toast.show('Erreur lors de la requête', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        return of(null as any as T);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }

  // ------------------------------
  // LIST / ALL (retours typés)
  // ------------------------------
  list<T>(model: string, page = 1, perPage = 10, opts?: BreadOptions): Observable<Paginated<T>> {
    return this.browse<T>(model, { ...(opts || {}), page, perPage, paginate: true }) as Observable<Paginated<T>>;
  }

  all<T>(model: string, opts?: BreadOptions): Observable<T[]> {
    return this.browse<T>(model, { ...(opts || {}), paginate: false }) as Observable<T[]>;
  }

  // ------------------------------
  // FIND / FIND BY
  // ------------------------------
  find<T>(model: string, id: number | string, opts?: BreadOptions): Observable<T> {
    return this.read<T>(model, id, { ...(opts || {}) });
  }

  findBy<T>(model: string, prop: string, value: any, opts?: BreadOptions): Observable<T | null> {
    return this.browse<T>(model, {
      ...(opts || {}),
      paginate: false,
      filters: { simple: [{ key: prop, value }] },
      mapResponse: (res: any) =>
        Array.isArray((res as any)?.data) ? ((res as any).data[0] ?? null) : null,
    }) as unknown as Observable<T | null>;
  }

  // ------------------------------
  // FILTER BY (overloads + impl)
  // ------------------------------
  filterBy<T>(model: string, filters: BreadFilter, opts?: BreadOptions): Observable<T[] | Paginated<T>> {
    const paginate = opts?.paginate ?? false;
    return this.browse<T>(model, {
      ...(opts || {}),
      filters,
      paginate,
    } as any) as Observable<T[] | Paginated<T>>;
  }

  // ------------------------------
  // ADD / EDIT / UPDATE / DELETE
  // ------------------------------
  add<T>(model: string, data: any, opts: Partial<BreadOptions> = {}): Observable<T> {
    const { prefix = 'monicase', endpoint = '/add/', skipLoader = false, beforeHook, afterHook, mapResponse, onError } = opts;

    const url = `${prefix}${endpoint}`;
    const useLoader = !skipLoader;

    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();

    // Détecter si c'est un FormData pour ajuster les headers
    const isFormData = data instanceof FormData;
    
    return this.api.post<T>(`${url}${model}`, data, '', isFormData).pipe(
      map(res => (mapResponse ? mapResponse(res) : ((res as any).data as T))),
      tap({
        error: err => {
          this.toast.show('Erreur à la création', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        return of(null as any as T);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }

  edit<T>(model: string, id: number | string, data: any, opts: Partial<BreadOptions> = {}): Observable<T> {
    const { prefix = 'monicase', endpoint = '/edit/', skipLoader = false, beforeHook, afterHook, mapResponse, onError } = opts;

    const url = `${prefix}${endpoint}`;
    const useLoader = !skipLoader;

    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();

    const isFormData = data instanceof FormData;
    return this.api.post<T>(`${url}${model}/${id}`, data, '', isFormData).pipe(
      map(res => (mapResponse ? mapResponse(res) : ((res as any).data as T))),
      tap({
        error: err => {
          this.toast.show('Erreur à la mise à jour', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        return of(null as any as T);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }

  /**
   * Met à jour une ressource par une clé autre que l'id (ex: name, slug).
   * URL: {prefix}{endpoint}{model}/{keyName}/{keyValue}
   */
  update<T>(model: string, keyName: string, keyValue: string | number, data: any, opts: Partial<BreadOptions> = {}): Observable<T> {
    const { prefix = 'monicase', endpoint = '/edit/', skipLoader = false, beforeHook, afterHook, mapResponse, onError } = opts;

    const url = `${prefix}${endpoint}`;
    const useLoader = !skipLoader;

    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();

    const isFormData = data instanceof FormData;
    const path = `${url}${model}/${keyName}`;
    return this.api.post<T>(path, data, '', isFormData).pipe(
      map(res => (mapResponse ? mapResponse(res) : ((res as any).data as T))),
      tap({
        error: err => {
          this.toast.show('Erreur à la mise à jour', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        return of(null as any as T);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }

  // Surcharge pour patch sans ID (ex: PATCH orders)
  patch<T>(model: string, data: any, opts: Partial<BreadOptions> & { endpoint?: string }): Observable<T>;
  // Surcharge pour patch avec ID (comportement standard)
  patch<T>(model: string, id: number | string | undefined, data: any, opts?: Partial<BreadOptions>): Observable<T>;
  patch<T>(model: string, idOrData: number | string | any, dataOrOpts?: any, opts?: Partial<BreadOptions>): Observable<T> {
    // Détecter si le deuxième paramètre est un ID ou des données
    const hasId = typeof idOrData === 'number' || typeof idOrData === 'string';
    const id = hasId ? idOrData : undefined;
    const data = hasId ? dataOrOpts : idOrData;
    const options = hasId ? (opts || {}) : (dataOrOpts || {});

    const { prefix = 'monicase', endpoint = '/edit/', skipLoader = false, beforeHook, afterHook, mapResponse, onError } = options;

    const useLoader = !skipLoader;
    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();

    const isFormData = data instanceof FormData;
    const url = endpoint === '' ? `${prefix}/${model}` : `${prefix}${endpoint}${model}`;
    const endpointUrl = id !== undefined ? `${url}/${id}` : url;

    return this.api.patch<T>(endpointUrl, data, '', isFormData).pipe(
      map(res => (mapResponse ? mapResponse(res) : ((res as any).data as T))),
      tap({
        error: err => {
          this.toast.show('Erreur à la mise à jour partielle', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        return of(null as any as T);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }

  delete<T>(model: string, data: any, opts: Partial<BreadOptions> = {}): Observable<T> {
    const { prefix = 'monicase', endpoint = '/delete/', skipLoader = false, beforeHook, afterHook, mapResponse, onError } = opts;

    const url = `${prefix}${endpoint}`;
    const useLoader = !skipLoader;

    if (useLoader) this.loader.setLoading(true);
    beforeHook?.();

    return this.api.post<T>(`${url}${model}`, data).pipe(
      map(res => (mapResponse ? mapResponse(res) : ((res as any).data as T))),
      tap({
        error: err => {
          this.toast.show('Erreur à la suppression', 'error');
          onError?.(err);
        }
      }),
      catchError(error => {
        this.toast.show('Erreur API', 'error');
        onError?.(error);
        return of(null as any as T);
      }),
      finalize(() => {
        if (useLoader) this.loader.setLoading(false);
        afterHook?.();
      })
    );
  }
}
